import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { assetRegisterApi } from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'it-asset-register-form';
const PRINTING_BODY_CLASS = 'sr-printing-asset-form';

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '';
}

function formatDate(value) {
  if (!value) return '';
  const s = String(value).trim();
  if (!s) return '';
  // Keep ISO date (yyyy-mm-dd) readable
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    try {
      const d = new Date(`${s.slice(0, 10)}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      }
    } catch {
      // fall through
    }
  }
  return s;
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

/**
 * Printable IT Asset Register detail form (same report style as Incident view).
 */
export default function AssetViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const queryId = searchParams.get('id');
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!queryId) {
      setAsset(null);
      setLoadError('Missing asset id.');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await assetRegisterApi.get(queryId);
        const data = unwrapData(res);
        if (!cancelled) {
          setAsset(data);
          if (!data) setLoadError('Asset not found.');
        }
      } catch (error) {
        if (!cancelled) {
          setAsset(null);
          setLoadError(apiErrorMessage(error, 'Failed to load asset details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryId]);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [
      isDark,
      tenantBranding?.backgroundColor,
      tenantBranding?.primaryColor,
      tenantBranding?.secondaryColor,
    ]
  );
  const tenantLogoSrc = useProfilePhotoSrc(tenantBranding?.tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  const assignedDisplay = useMemo(() => {
    if (!asset) return '';
    if (asset.assignedTo) return asset.assignedTo;
    if (Array.isArray(asset.assignedUsers) && asset.assignedUsers.length) {
      return asset.assignedUsers
        .map((u) => u.name || u.email)
        .filter(Boolean)
        .join(', ');
    }
    return '';
  }, [asset]);

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl || !asset || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      const labelBase = (asset.assetId || asset.assetName || 'asset')
        .replace(/[^\w\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 48) || 'asset';
      await downloadFormPdf({
        element: formEl,
        fileName: `${labelBase}-IT-ASSET-FORM.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [asset, downloadingPdf]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-asset-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          margin: 0 !important;
          box-shadow: none !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!asset) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="IT Asset Register Form" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1">
            {loadError || 'Open an asset from the IT Asset Register using the view action.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header title="IT Asset Register Form" />
      </Box>

      <Box
        className="no-print"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Button type="button" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} variant="outlined">
          Print
        </Button>
        <Button
          type="button"
          startIcon={
            downloadingPdf ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PictureAsPdfOutlinedIcon />
            )
          }
          onClick={handleDownloadPdf}
          variant="contained"
          color="secondary"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark
            ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}`
            : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography
                sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}
              >
                LOGO
              </Typography>
            )}
          </Box>

          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>

          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              IT Asset Register Form
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR AR1
            </Typography>
          </Box>
        </Box>

        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Asset ID:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {dash(asset.assetId) || '—'}
              </FieldCell>
              <FieldCell label="Asset Tag:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(asset.assetTag) || '—'}
              </FieldCell>
              <FieldCell label="Asset Type:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(asset.assetType) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Asset Name:" border={form.border} muted={form.muted} colSpan={2}>
                {dash(asset.assetName) || '—'}
              </FieldCell>
              <FieldCell label="Serial Number:" border={form.border} muted={form.muted}>
                {dash(asset.serialNumber) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Manufacturer:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {dash(asset.manufacturer) || '—'}
              </FieldCell>
              <FieldCell label="Model:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {dash(asset.model) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Assigned To:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(assignedDisplay) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Department:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {dash(asset.department) || '—'}
              </FieldCell>
              <FieldCell label="Location:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {dash(asset.location) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Purchase Date:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {formatDate(asset.purchaseDate) || '—'}
              </FieldCell>
              <FieldCell label="Warranty Expiry:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {formatDate(asset.warrantyExpiry) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Asset Status:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {formatEnumLabel(asset.assetStatus) || '—'}
              </FieldCell>
              <FieldCell label="Asset Condition:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {formatEnumLabel(asset.assetCondition) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Remarks:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(asset.remarks) || '—'}</Box>
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
